/* The signed-out pages: sign-in, the second factor, joining a workspace, resetting a password, and the
   state where the session cannot be read. One page, one centred card, one stylesheet - they are one
   journey for a person who has never seen the product, and four stylings of "a form on a bare page"
   would drift (this file replaces `SignIn.module.css`, `Join.module.css` and
   `ResetPassword.module.css`, which said so in their own headers).

   **The language is the shell's, not a new one.** The page is the rail's forest gradient and the brand
   is the rail's mark and wordmark, so the first thing a person meets is recognisably the thing they
   will sign in to. The card is the app's panel surface (`--panel`, `--radius-card`, `--shadow-panel`).
   Every colour is a token; the pairs used here (`--rail-ink` and `--rail-ink-2` on the rail
   surfaces, `--ink`/`--ink-2`/`--muted` on `--panel`, `--crit` on `--crit-w`) are the ones
   `test_design_tokens_contrast.py` already measures. */

/* **This page scrolls itself, because the document may not.** `tokens.css` sets `overflow: hidden`
   on `html`, `body` and `#root` so the app shell never page-scrolls. The join form (name, two
   passwords, a paragraph, then the submit button) is taller than most laptop windows, so without
   this "Create my account and join" sat below a fold nothing could scroll to (found by the first
   end-to-end walkthrough, 2026-10-29). The grid row still grows with its content, so a short card
   stays centred and a tall one is reached from its top rather than clipped above it. */
.page {
  display: grid;
  place-items: center;
  height: 100%;
  overflow: hidden auto;
  padding: 32px 16px;
  background: linear-gradient(180deg, var(--rail-bg-top), var(--rail-bg-bottom));
}

.column {
  display: grid;
  gap: 24px;
  width: 100%;
  max-width: 440px;
  min-width: 0;
}

/* ------------------------------------------------------------------------------------ brand */

.brand {
  display: grid;
  gap: 6px;
  justify-items: center;
  text-align: center;
}

.lockup {
  display: flex;
  align-items: center;
  gap: 12px;
}

/* A letter on the accent, exactly as the rail draws it: the mark is the product's name and not an
   icon, and a hand-drawn glyph standing in for a logo nobody supplied would be a placeholder. */
.mark {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  flex-shrink: 0;
  border-radius: var(--radius-chip);
  background: var(--rail-accent);
  color: var(--rail-bg);
  font-size: 20px;
  font-weight: 700;
}

.wordmark {
  font-size: 28px;
  font-weight: 700;
  letter-spacing: -0.02em;
  color: var(--rail-ink);
}

.tag {
  font-size: var(--t-body);
  color: var(--rail-ink-2);
}

/* ------------------------------------------------------------------------------------ card */

.card {
  min-width: 0;
  background: var(--panel);
  color: var(--ink);
  border-radius: var(--radius-card);
  box-shadow: var(--shadow-panel);
}

.head {
  display: grid;
  gap: 4px;
  padding: 24px 24px 0;
}

/* Focus is moved here when the step changes, so it must be visible when it lands. It is a heading,
   not a control, so it takes the ring without the field's inner offset. */
.title {
  margin: 0;
  font-size: 22px;
  font-weight: 650;
  line-height: 1.25;
  letter-spacing: -0.01em;
  color: var(--ink);
  overflow-wrap: anywhere;
}

.title:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 4px;
  border-radius: var(--radius-ctl);
}

.sub {
  margin: 0;
  font-size: var(--t-body);
  color: var(--muted);
}

.body {
  display: grid;
  gap: 16px;
  padding: 20px 24px 24px;
}

/* ------------------------------------------------------------------------------------ notice */

/* An error is **an icon and words**, never a red box alone: colour is not the only signal. It sits
   above the form so it is on screen on a phone without scrolling, and it is a live region so it is
   announced when it appears. */
.notice {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  gap: 10px;
  align-items: start;
  margin: 0;
  padding: 12px 14px;
  font-size: var(--t-body);
  line-height: 1.5;
  color: var(--crit);
  background: var(--crit-w);
  border: 1px solid var(--crit-line);
  border-radius: var(--radius-ctl);
}

.noticeIcon {
  width: 20px;
  height: 20px;
  margin-top: 1px;
  fill: currentcolor;
}

.noticeText {
  min-width: 0;
  overflow-wrap: anywhere;
}

/* Links and the one repair button inside a notice take the notice's own ink: the accent fails on
   its tinted background. Underlined, because colour is never the only signal. */
.notice a,
.notice .link {
  color: inherit;
  text-decoration: underline;
}

/* ------------------------------------------------------------------------------------ form */

.form {
  display: grid;
  gap: 16px;
}

.field {
  display: grid;
  gap: 6px;
  font-size: var(--t-body);
  font-weight: 600;
  color: var(--ink-2);
}

/* 16px, always: a phone zooms the whole page when a field under 16px takes focus, and these pages
   are reached on a phone, from an email, by someone who has never seen them. The border is `--muted`
   rather than `--line`: a field's edge is a graphical object that has to reach 3:1 against the card
   (WCAG 1.4.11), and `--line` is under 1.5:1. */
.field input {
  width: 100%;
  min-height: 44px;
  padding: 10px 12px;
  font: inherit;
  font-size: 16px;
  font-weight: 400;
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--muted);
  border-radius: var(--radius-ctl);
}

.field input:hover:not(:disabled) {
  border-color: var(--ink-2);
}

.field input:focus-visible {
  outline: 2px solid var(--focus);
  outline-offset: 1px;
  border-color: var(--accent);
}

.field input:disabled {
  color: var(--muted);
  background: var(--panel-2);
}

.field input[aria-invalid="true"] {
  border-color: var(--crit);
}

.primary,
.secondary {
  display: block;
  width: 100%;
  min-height: 44px;
  padding: 10px 16px;
  font: inherit;
  font-size: var(--t-head);
  font-weight: 600;
  text-align: center;
  text-decoration: none;
  border-radius: var(--radius-ctl);
  cursor: pointer;
}

.primary {
  color: var(--on-fill);
  background: var(--accent);
  border: 1px solid var(--accent);
}

.primary:hover:not(:disabled) {
  background: var(--accent-h);
}

.secondary {
  color: var(--ink);
  background: var(--panel);
  border: 1px solid var(--muted);
}

.secondary:hover:not(:disabled) {
  background: var(--panel-2);
}

/* Said by the label ("Checking...") and by the cursor, and not by colour alone. */
.primary:disabled,
.secondary:disabled {
  opacity: 0.65;
  cursor: progress;
}

/* The way back out of a stage, and the way to let the server answer after an interrupted request. A
   link in appearance because it is a repair rather than a decision, and a real `<button>` because it
   does something. */
.link {
  padding: 0;
  font: inherit;
  font-size: var(--t-body);
  color: var(--accent);
  text-align: center;
  text-decoration: underline;
  background: none;
  border: 0;
  cursor: pointer;
}

.link:disabled {
  color: var(--muted);
  cursor: default;
}

/* "Use a passkey" beneath the password form (ADR-0105): a second way in, never a replacement. The
   rule and the word say "or" without the passkey button looking like the main action. */
.divider {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0;
  font-size: var(--t-dense);
  color: var(--muted);
}

.divider::before,
.divider::after {
  content: "";
  flex: 1;
  border-top: 1px solid var(--line-2);
}

.prose {
  margin: 0;
  font-size: var(--t-body);
  line-height: 1.55;
  color: var(--ink-2);
}

/* The links beneath a form ("Have an invitation?", "Forgot your password?"). */
.aside {
  display: grid;
  gap: 8px;
  margin: 0;
  padding-top: 16px;
  font-size: var(--t-body);
  color: var(--ink-2);
  border-top: 1px solid var(--line);
}

.aside p {
  margin: 0;
}

.prose a,
.aside a {
  color: var(--accent);
  text-decoration: underline;
}

/* **A thumb-sized target for the links that are a line on their own** (responsive pass,
   2026-10-30). "Forgot your password?", "Join a workspace" and "Back to sign in" were 16px tall at
   390px — the only controls on these pages under 44px, and on the page a phone shows first. The
   padding is cancelled by an equal negative margin, so nothing moves by a pixel and only the region
   that answers a tap grows. */
.aside a,
.link {
  display: inline-block;
  padding-block: 14px;
  margin-block: -14px;
}

/* ------------------------------------------------------------------------------------ facts */

.facts {
  display: grid;
  gap: 10px;
  margin: 0;
  padding: 12px 14px;
  background: var(--panel-2);
  border: 1px solid var(--line);
  border-radius: var(--radius-ctl);
}

.facts > div {
  display: grid;
  gap: 2px;
}

.facts dt {
  font-size: var(--t-min);
  color: var(--muted);
}

.facts dd {
  margin: 0;
  font-size: var(--t-body);
  font-weight: 600;
  color: var(--ink);
  overflow-wrap: anywhere;
}

/* The setup key and the recovery codes. Monospaced and selectable, because they are transcribed -
   and `user-select: all` so one click takes the whole value rather than part of it. The most
   legibility-critical type in the product: a misread character means starting over. */
.secret,
.codes code {
  display: block;
  padding: 10px 12px;
  font-family: var(--mono);
  font-size: var(--t-head);
  letter-spacing: 0.02em;
  color: var(--ink);
  background: var(--panel-2);
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  user-select: all;
}

.secret {
  word-break: break-all;
}

.codes {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(min(100%, 140px), 1fr));
  gap: 6px;
  margin: 0;
  padding: 0;
  list-style: none;
}

@media (max-width: 480px) {
  .page {
    padding: 20px 12px;
  }

  .head {
    padding: 20px 18px 0;
  }

  .body {
    padding: 16px 18px 20px;
  }

  .wordmark {
    font-size: 24px;
  }
}
